import { memo } from "react";
import clsx from "clsx";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import { Marker } from "react-mapbox-gl";

import { palette } from "../../theme/palette";

import { getSelectedDepot } from "../../reducers/ownApp/selectors";
import { getUserDepots } from "../../reducers/ownUser/selectors";
import { DPDLogoIcon } from "../Icons";
import { View } from "../View";
import { setSelectedDepot } from "../../reducers/ownApp/thunkActions";
import { setZoomFromMouseEvent } from "../../reducers/map/thunkActions";
import { isEditModeSelector } from "../../reducers/matrixProfile/selectors";
import { driverEditModeSelector } from "../../reducers/driverManagement/selectors";

import { useLocationDetails } from "../../hooks/useLocationDetails";

const useStyles = makeStyles({
  marker: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  label: {
    color: palette.grey,
    fontWeight: 400,
  },
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "2rem",
    width: "2rem",
    borderStyle: "solid",
    borderWidth: "0.2rem",
    borderColor: "white",
    borderRadius: "0.5rem",
    boxShadow: `0px 2px 3px ${palette.typography.black.high}`,
  },
  selectedDepotContainer: {
    borderColor: palette.darkGreen,
  },
  dpdRedContainer: {
    backgroundColor: palette.dpdRed,
  },
  dpdLocalContainer: {
    backgroundColor: palette.dpdLocal,
  },
  dpdUrbanContainer: {
    backgroundColor: palette.dpdUrban,
  },
  arrowContainer: {
    display: "flex",
    justifyContent: "center",
  },
  arrow: {
    width: "0",
    height: "0",
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderTop: "10px solid white",
  },
  selectedDepotArrow: {
    borderTop: `10px solid ${palette.darkGreen}`,
  },
});

export const DepotLabelLayer = memo(() => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const selectedDepot = useSelector(getSelectedDepot);
  const editMode = useSelector(isEditModeSelector);
  const driverEditMode = useSelector(driverEditModeSelector);
  const depots = useSelector(getUserDepots);
  const { application } = useLocationDetails();

  const getDepotContainerStyles = ({ businessUnit, depotCode }) => {
    const isSelected = depotCode === selectedDepot;
    if (businessUnit === 3)
      return clsx(
        styles.container,
        styles.dpdUrbanContainer,
        isSelected && styles.selectedDepotContainer
      );
    else if (businessUnit === 2)
      return clsx(
        styles.container,
        styles.dpdLocalContainer,
        isSelected && styles.selectedDepotContainer
      );
    else
      return clsx(
        styles.container,
        styles.dpdRedContainer,
        isSelected && styles.selectedDepotContainer
      );
  };

  return (
    <>
      {depots.map(
        ({
          depotCode,
          depotName,
          businessUnit,
          latitude: lat,
          longitude: lng,
        }) => (
          <Marker
            key={`${depotCode}-${lat}-${lng}`}
            className={styles.marker}
            coordinates={{ lat, lng }}
            anchor={"bottom"}
            onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
          >
            <View
              className={styles.label}
            >{`${depotName} - ${depotCode}`}</View>
            <View
              className={getDepotContainerStyles({ businessUnit, depotCode })}
              onClick={() =>
                !editMode &&
                !driverEditMode &&
                dispatch(setSelectedDepot({ value: depotCode, application }))
              }
            >
              <DPDLogoIcon />
            </View>
            <View className={styles.arrowContainer}>
              <View
                className={clsx(
                  styles.arrow,
                  depotCode === selectedDepot && styles.selectedDepotArrow
                )}
              />
            </View>
          </Marker>
        )
      )}
    </>
  );
});
